"use client";

import { Box, Card, CardContent, Container, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { PAGE_ROUTES } from "@/app/_utils/constants";
import ScrollReveal from "@/app/components/ScrollReveal";
import { trackWorkWithUsClick } from "@/app/_utils/workWithUsAnalytics";

const pageFont = '"Inter", "Helvetica Neue", -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif';

const cards = [
  {
    image: "/images/home-card-public-shows.png",
    title: "Public Shows",
    description:
      "Ticketed pop-up comedy and magic experiences at NYC's most iconic venues. Secret lineups, all-inclusive tickets, unforgettable nights.",
    ctaText: "Get Tickets",
    href: PAGE_ROUTES.TICKETS,
  },
  {
    image: "/images/home-card-private-events.png",
    title: "Private Events",
    description:
      "Corporate gatherings, birthday celebrations, and group bookings. Tell us the headcount, we handle the rest.",
    ctaText: "Book Event",
    href: PAGE_ROUTES.PRIVATE_EVENTS,
  },
  {
    image:
      "https://storage.googleapis.com/cuo-website/AriZona+Product.jpg",
    title: "Brand Partnerships",
    description:
      "Integrated sponsorships and experiential activations. Partner with us to reach engaged NYC audiences.",
    ctaText: "Partner With Us",
    href: PAGE_ROUTES.WORK_WITH_US,
  },
];

export default function FeatureCardsSection() {
  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: "#0d0d0d",
        py: { xs: 6, md: 10 },
        px: { xs: 2, md: 4 },
        boxSizing: "border-box",
      }}
    >
      <Container maxWidth="lg">
        <ScrollReveal variant="fadeUp">
          <Typography
            sx={{
              fontFamily: pageFont,
              fontSize: "0.75rem",
              fontWeight: 600,
              letterSpacing: "0.15em",
              color: "rgba(255,255,255,0.6)",
              textTransform: "uppercase",
              mb: 1,
            }}
          >
            What We Do
          </Typography>
          <Typography
            component="h2"
            sx={{
              fontFamily: pageFont,
              fontWeight: 700,
              fontSize: { xs: "1.75rem", md: "2.25rem" },
              color: "#fff",
              mb: 5,
              lineHeight: 1.2,
            }}
          >
            Premium Experiences, Curated for You
          </Typography>
        </ScrollReveal>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
            gap: 4,
          }}
        >
          {cards.map((card, i) => (
            <ScrollReveal key={card.href} variant="fadeUp" delay={i * 0.12}>
            <Link
              to={card.href}
              onClick={() => {
                if (card.href === PAGE_ROUTES.WORK_WITH_US) trackWorkWithUsClick();
              }}
              style={{ textDecoration: "none", display: "block", height: "100%" }}
            >
              <Card
                sx={{
                  height: "100%",
                  backgroundColor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "12px",
                  overflow: "hidden",
                  transition: "transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    borderColor: "rgba(255,255,255,0.18)",
                    boxShadow: "0 12px 40px rgba(0,0,0,0.4)",
                  },
                }}
              >
                <Box
                  sx={{
                    height: { xs: 280, md: 360 },
                    backgroundImage: `url('${card.image}')`,
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                  }}
                />
                <CardContent sx={{ p: 2.5 }}>
                  <Typography
                    sx={{
                      fontFamily: pageFont,
                      fontWeight: 700,
                      fontSize: "1.25rem",
                      color: "#fff",
                      mb: 1.5,
                    }}
                  >
                    {card.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: pageFont,
                      fontSize: "0.95rem",
                      lineHeight: 1.6,
                      color: "rgba(255,255,255,0.75)",
                      mb: 2,
                    }}
                  >
                    {card.description}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      color: "#fff",
                      fontFamily: pageFont,
                      fontWeight: 600,
                      fontSize: "0.9rem",
                    }}
                  >
                    {card.ctaText}
                    <ArrowForwardIcon sx={{ fontSize: 18 }} />
                  </Box>
                </CardContent>
              </Card>
            </Link>
            </ScrollReveal>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
